import React, { FC, useEffect, useRef, useState } from 'react'
import { ShareIcon } from '../../../../constants/svg'
import ImgUtil from '../../../../utils/img.utils'
import { findTarget } from '../../../../utils/microcopy.utils'
import { renderRichText } from '../../../../utils/richTextHandler.utils'
import formatInlineCode from '../../../../utils/shortTextFormatting'
import Typography from '../../typography'
import VideoPlayer from '../../videoPlayer'
import { VideoCard, VideoSpeakerList } from '../style'

type Props = {
  data: any
  index: number
  openModal: any
}

const VideoCardList: FC<Props> = ({ data, index, openModal }) => {
  var cardRef = useRef<HTMLDivElement>(null)
  const [allSpeakers, setShowAllSpeakers] = useState(false)
  const [showMore, setShowMore] = useState(false)

  const [isDescOverflow, setIsDescOverflow] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)

  const showAllSpeakers = () => {
    setShowAllSpeakers(!allSpeakers)
  }

  const recordingData = {
    id: data?.id || '',
    category: data?.label || '',
    title: data?.title || '',
    description: data?.description || '',
    speakers: data?.speakers || [],
    microcopySetValues: data?.microcopy?.values || [],
    shareText: findTarget(`recording.share.video.text`, data?.microcopy?.values, true) || '',
    allSpeakers: findTarget(`recording.speaker.more.text`, data?.microcopy?.values, true) || '',
    lessSpeakers: findTarget(`recording.speaker.less.text`, data?.microcopy?.values, true) || '',
    readLess: findTarget(`recording.read.less.text`, data?.microcopy?.values, true) || '',
    readMore: findTarget(`recording.read.more.text`, data?.microcopy?.values, true) || '',

    video: {
      image: data?.video?.image?.file?.url || '',
      altText: data?.video?.altText || '',
      start: data?.video?.start || '',
      animationFile: data?.video?.animationFile?.file?.url || '',
      url: '',
      id: '',
      type: '',
    },
  }

  if (data?.video?.youtubeId) {
    recordingData.video.id = data.video.youtubeId
    recordingData.video.type = 'youtube'
  }

  if (data?.video?.vimeoId) {
    recordingData.video.id = data.video.vimeoId
    recordingData.video.type = 'vimeo'
  }

  if (data?.video?.videoUrl) {
    recordingData.video.url = data.video.videoUrl
    recordingData.video.type = 'html-video'
  }

  if (data?.video?.tencentVideoUrl) {
    recordingData.video.url = data.video.tencentVideoUrl
    recordingData.video.type = 'tencent-video'
  }

  const renderSpeakerList = (speakersData: any) => {
    return speakersData?.map((speaker: any, index: number) => {
      return (
        <div className="speaker" key={speaker?.id}>
          <div className="speaker-img">
            <img
              className="Headshot__Image"
              src={ImgUtil.getResizedImageUrl(speaker?.headshot?.image?.file?.url, 'width=430')}
              alt={speaker?.headshot?.altText}
            />
          </div>
          <div className="speaker-info">
            <div className="name">
              {speaker?.firstName} {speaker?.lastName}
            </div>
            <div className="desg">{speaker?.role}</div>
            <div className="company">{speaker?.company}</div>
          </div>
        </div>
      )
    })
  }

  const shareModalData = {
    title:
      findTarget(`recording.share.popup.title.text`, recordingData?.microcopySetValues, true) || '',
    ctaText:
      findTarget(`recording.share.popup.button.text`, recordingData?.microcopySetValues, true) ||
      '',
    successMessage:
      findTarget(`recording.share.popup.success.text`, recordingData?.microcopySetValues, true) ||
      '',
    videoId: recordingData?.video?.id || recordingData?.video?.url.split('/')[4],
    position: {
      top: 0,
      left: 0,
    },
  }

  const videoShareLink = (e: React.MouseEvent<HTMLDivElement>) => {
    const cardRect = (e.target as HTMLDivElement).getBoundingClientRect()
    const top = cardRect.top + window.scrollY
    const left = cardRect.left + window.scrollX

    shareModalData.position = {
      top: top,
      left: left,
    }

    openModal(shareModalData)
  }

  const showMoreDescription = () => {
    setShowMore(!showMore)
  }

  useEffect(() => {
    // Check if the text is overflowing
    const checkOverflow = () => {
      if (textRef.current) {
        setIsDescOverflow(textRef.current.scrollHeight > textRef.current.clientHeight)
      }
    }

    // Check overflow on mount and window resize
    checkOverflow()
    window.addEventListener('resize', checkOverflow)

    return () => {
      window.removeEventListener('resize', checkOverflow)
    }
  }, [])

  return (
    <VideoCard className="Video__Card" $descShowMore={showMore}>
      <div className="Video__Card-Box">
        <VideoPlayer
          width={'100%'}
          height="auto"
          videoId={recordingData?.video?.id}
          videoUrl={recordingData?.video?.url}
          type={recordingData?.video?.type}
          img={recordingData?.video?.image}
          backgroundAltText={recordingData?.video?.altText}
          backgroundImgParams={'width=732'}
          start={recordingData?.video?.start}
          animationFileUrl={recordingData?.video?.animationFile}
        />
      </div>
      <div className="Video__Card-Content">
        <div className="Video__Social">
          <Typography className="Video__Category" type="captionAlt">
            {recordingData?.category}
          </Typography>
          <div className="Video__Share" onClick={videoShareLink} ref={cardRef}>
            {recordingData?.shareText} <ShareIcon />
          </div>
        </div>

        <Typography className="Video__Title" type="header3">
          {formatInlineCode(recordingData?.title)}
        </Typography>

        <div className="Video__Description" ref={textRef}>
          {renderRichText(recordingData?.description)}
        </div>

        {isDescOverflow && (
          <div className="Description__MoreLess">
            {showMore ? (
              <span className="text" onClick={showMoreDescription}>
                {recordingData?.readLess}
              </span>
            ) : (
              <span className="text" onClick={showMoreDescription}>
                {recordingData?.readMore}
              </span>
            )}
          </div>
        )}
        <VideoSpeakerList className="Video__SpeakerList">
          {recordingData?.speakers?.length > 0 && (
            <div className="Video__SpeakerList-Top">
              {renderSpeakerList(recordingData?.speakers.slice(0, 4))}
            </div>
          )}

          {recordingData?.speakers?.length > 3 && (
            <>
              {allSpeakers && (
                <div className="Video__SpeakerList-Bottom">
                  {renderSpeakerList(recordingData?.speakers.slice(4))}
                </div>
              )}
              {recordingData?.speakers?.length > 4 && (
                <div className="Video__Speaker-All">
                  {allSpeakers ? (
                    <span className="text" onClick={showAllSpeakers}>
                      {recordingData?.lessSpeakers}
                    </span>
                  ) : (
                    <span className="text" onClick={showAllSpeakers}>
                      {recordingData?.allSpeakers}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </VideoSpeakerList>
      </div>
    </VideoCard>
  )
}

export default VideoCardList
